import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, CheckCircle2, AlertCircle, Loader2, Upload, ArrowUpRight, Sparkles } from 'lucide-react';
import { Photo, Room } from '../../types';
import { enqueuePhotoUploads, retryFailedUploads, discardFailedUploads } from '../../services/uploadQueue';
import { useUploadQueue } from '../../hooks/useUploadQueue';
import { Button } from '../ui/Button';

/** Neutral placeholder for a photo whose URL is missing or failed to load. */
const PHOTO_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect width="400" height="300" fill="#ece8e1"/><path d="M140 190l40-50 30 36 24-28 46 42H140z" fill="#c9c2b6"/><circle cx="262" cy="118" r="16" fill="#c9c2b6"/></svg>'
  );

interface PhotoPanelProps {
  projectId: string;
  rooms: Room[];
  photos: Photo[];
  /** Called after each upload lands so the caller can refetch. */
  onUploaded: () => void;
  /** Receives a function that opens the file picker, so a header button can trigger it. */
  pickerRef?: React.MutableRefObject<(() => void) | null>;
  className?: string;
}

type LocalJob = { id: string; name: string; status: 'queued' | 'done' | 'failed' };

const ACCEPT = 'image/*';
const MAX_PREVIEW = 60;

/**
 * Desktop photo panel: thumbnails grouped by room, a drop zone plus a file
 * picker, and page-wide drag-and-drop. Every file goes through the existing
 * background upload queue untouched; this component only hands it blobs and
 * listens for completion.
 */
export function PhotoPanel({ projectId, rooms, photos, onUploaded, pickerRef, className = '' }: PhotoPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!pickerRef) return;
    pickerRef.current = () => inputRef.current?.click();
    return () => { pickerRef.current = null; };
  }, [pickerRef]);
  const [dragging, setDragging] = useState(false);
  const [jobs, setJobs] = useState<LocalJob[]>([]);
  const [targetRoom, setTargetRoom] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const queue = useUploadQueue();
  const dragDepth = useRef(0);

  const roomName = useMemo(() => {
    const map = new Map<string, string>();
    rooms.forEach(r => map.set(r.id, r.name));
    return map;
  }, [rooms]);

  const grouped = useMemo(() => {
    const groups = new Map<string, Photo[]>();
    photos.forEach(p => {
      const key = p.roomId && roomName.has(p.roomId) ? p.roomId : '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    });
    const ordered = rooms.map(r => [r.id, groups.get(r.id) ?? []] as const).filter(([, list]) => list.length > 0);
    if (groups.has('')) ordered.push(['', groups.get('')!] as const);
    return ordered;
  }, [photos, rooms, roomName]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const images = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (images.length === 0) return;
    const target = targetRoom ? { projectId, roomId: targetRoom } : { projectId };
    const ids = enqueuePhotoUploads(images, target, {
      onUploaded: (jobId) => {
        setJobs(prev => prev.map(j => (j.id === jobId ? { ...j, status: 'done' } : j)));
        onUploaded();
      },
      onFailed: (jobId) => {
        setJobs(prev => prev.map(j => (j.id === jobId ? { ...j, status: 'failed' } : j)));
      },
    });
    setJobs(prev => [...prev, ...ids.map((id, i) => ({ id, name: images[i].name, status: 'queued' as const }))]);
  }, [projectId, targetRoom, onUploaded]);

  // Page-wide drag: dropping anywhere on the workspace adds to this project.
  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const onEnter = (e: DragEvent) => { if (!hasFiles(e)) return; dragDepth.current += 1; setDragging(true); };
    const onLeave = (e: DragEvent) => { if (!hasFiles(e)) return; dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); } };
    const onOver = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
    };
    document.addEventListener('dragenter', onEnter);
    document.addEventListener('dragleave', onLeave);
    document.addEventListener('dragover', onOver);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragenter', onEnter);
      document.removeEventListener('dragleave', onLeave);
      document.removeEventListener('dragover', onOver);
      document.removeEventListener('drop', onDrop);
    };
  }, [addFiles]);

  // Clear the finished list once the batch is fully done and nothing failed.
  useEffect(() => {
    if (jobs.length > 0 && jobs.every(j => j.status === 'done')) {
      const t = setTimeout(() => setJobs([]), 4000);
      return () => clearTimeout(t);
    }
  }, [jobs]);

  const queued = jobs.filter(j => j.status === 'queued').length;
  const done = jobs.filter(j => j.status === 'done').length;
  const failed = jobs.filter(j => j.status === 'failed').length;
  const visiblePhotos = showAll ? photos : photos.slice(0, MAX_PREVIEW);
  const analyzing = photos.filter(p => p.aiLens?.status === 'analyzing' || p.aiLens?.status === 'pending').length;

  return (
    <section id="foton" aria-labelledby="foton-heading" className={`rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)] ${className}`}>
      <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[var(--border-subtle)]">
        <div className="flex items-baseline gap-3">
          <h2 id="foton-heading" className="text-[17px] font-semibold text-[var(--text-primary)]">Foton</h2>
          <span className="text-sm text-[var(--text-secondary)] tabular-nums">
            {photos.length === 0 ? 'Inga foton än' : `${photos.length} foton`}
            {analyzing > 0 && ` · ${analyzing} analyseras`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {rooms.length > 0 && (
            <label className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
              Lägg i rum
              <select
                value={targetRoom}
                onChange={(e) => setTargetRoom(e.target.value)}
                className="h-[32px] rounded-ios-control border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[13px] text-[var(--text-primary)]"
              >
                <option value="">Inget rum</option>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
          )}
          <Button variant="secondary" size="sm" icon={<Upload size={14} />} onClick={() => inputRef.current?.click()}>
            Ladda upp foton
          </Button>
          {photos.length > 0 && (
            <Link
              to={`/projects/${projectId}/photos`}
              className="inline-flex items-center gap-1 h-[32px] px-2.5 rounded-ios-control text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] hover:text-[var(--text-primary)]"
            >
              Hantera foton <ArrowUpRight size={14} />
            </Link>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="sr-only"
          aria-label="Välj foton att ladda upp"
          onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
        />
      </header>

      {/* Batch progress: visible while anything is queued or failed. */}
      {jobs.length > 0 && (
        <div className="px-5 py-3 border-b border-[var(--border-subtle)] bg-[var(--bg-primary)]/60">
          <div className="flex items-center justify-between gap-4 text-sm">
            <div className="flex items-center gap-2 text-[var(--text-primary)]">
              {queued > 0 ? <Loader2 size={16} className="animate-spin text-accent-600" aria-hidden="true" /> : failed > 0 ? <AlertCircle size={16} className="text-red-700 dark:text-red-300" aria-hidden="true" /> : <CheckCircle2 size={16} className="text-emerald-700 dark:text-emerald-300" aria-hidden="true" />}
              <span aria-live="polite">
                {queued > 0
                  ? `Laddar upp ${done + 1} av ${jobs.length}…`
                  : failed > 0
                    ? `${failed} ${failed === 1 ? 'foto' : 'foton'} kunde inte laddas upp`
                    : `${done} ${done === 1 ? 'foto uppladdat' : 'foton uppladdade'}`}
              </span>
            </div>
            {failed > 0 && queued === 0 && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => { retryFailedUploads(); setJobs(prev => prev.map(j => j.status === 'failed' ? { ...j, status: 'queued' } : j)); }}>Försök igen</Button>
                <Button size="sm" variant="ghost" onClick={() => { discardFailedUploads(); setJobs(prev => prev.filter(j => j.status !== 'failed')); }}>Ta bort</Button>
              </div>
            )}
          </div>
          {queued > 0 && (
            <div className="mt-2 h-1.5 rounded-full bg-[var(--bg-surface-alt)] overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={jobs.length} aria-valuenow={done}>
              <div className="h-full bg-accent-600 transition-[width] duration-300" style={{ width: `${Math.round((done / jobs.length) * 100)}%` }} />
            </div>
          )}
        </div>
      )}

      <div className="p-5">
        {photos.length === 0 && jobs.length === 0 ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={`w-full rounded-ios-card border-2 border-dashed px-6 py-10 text-center transition-colors duration-150 ${dragging ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20' : 'border-[var(--border-default)] hover:border-[var(--text-tertiary)] hover:bg-[var(--bg-surface-hover)]'}`}
          >
            <Camera size={28} className="mx-auto mb-3 text-[var(--text-secondary)]" aria-hidden="true" />
            <p className="text-sm font-medium text-[var(--text-primary)]">Släpp foton här, eller klicka för att välja</p>
            <p className="mt-1 text-[13px] text-[var(--text-secondary)]">Upp till ett hundratal bilder åt gången. Hämtteamet ser dem i appen.</p>
          </button>
        ) : (
          <div className="space-y-5">
            {grouped.map(([roomId, list]) => {
              const shown = list.filter(p => visiblePhotos.includes(p));
              if (shown.length === 0) return null;
              return (
                <div key={roomId || 'none'}>
                  <h3 className="mb-2 text-[13px] font-semibold text-[var(--text-secondary)]">
                    {roomId ? roomName.get(roomId) : 'Utan rum'} <span className="font-normal tabular-nums">· {list.length}</span>
                  </h3>
                  <ul className="grid grid-cols-6 xl:grid-cols-8 gap-2">
                    {shown.map(p => (
                      <li key={p.id} className="relative aspect-[4/3] overflow-hidden rounded-ios-control bg-[var(--bg-surface-alt)]">
                        <Link to={`/projects/${projectId}/photos`} aria-label={p.aiLens?.result?.objectName ? `Foto: ${p.aiLens.result.objectName}` : 'Foto'}>
                          <img
                            src={p.thumbnail || p.url || PHOTO_PLACEHOLDER}
                            alt={p.aiLens?.result?.objectName ?? ''}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover transition-transform duration-200 hover:scale-[1.03]"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = PHOTO_PLACEHOLDER; }}
                          />
                        </Link>
                        {p.aiLens?.status === 'completed' && (
                          <span className="absolute left-1 top-1 inline-flex items-center gap-0.5 rounded bg-black/55 px-1 py-0.5 text-[12px] font-medium text-white" title="AI Lens-analyserad">
                            <Sparkles size={10} aria-hidden="true" />
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {photos.length > MAX_PREVIEW && !showAll && (
              <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>Visa alla {photos.length} foton</Button>
            )}
          </div>
        )}
      </div>

      {/* Page-wide drop overlay. */}
      {dragging && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[var(--bg-primary)]/80 backdrop-blur-sm pointer-events-none" aria-hidden="true">
          <div className="rounded-ios-sheet border-2 border-dashed border-accent-500 bg-[var(--bg-surface)] px-10 py-8 text-center shadow-ios-elevated">
            <Upload size={32} className="mx-auto mb-3 text-accent-600" />
            <p className="text-[17px] font-semibold text-[var(--text-primary)]">Släpp för att ladda upp till projektet</p>
            {queue.pendingCount > 0 && <p className="mt-1 text-sm text-[var(--text-secondary)]">{queue.pendingCount} i kö redan</p>}
          </div>
        </div>
      )}
    </section>
  );
}

export default PhotoPanel;
